import { getAuthHeaders } from './authService';

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || '';

// Deleting an account and downloading its data (#452).
//
// A brand owner's deletion is scheduled: the account locks at once and is
// purged 30 days later, unless the owner signs in and restores it. A team
// member's deletion (leaving the team) is immediate. Both re-enter their
// password, checked by the API.

/** A brand owner's pending deletion, on /users/me while it is scheduled. */
export interface PendingDeletion {
  requestedAt: string;
  /** When the purge runs (30 days after the request). */
  purgeAfter: string;
}

export type DeletionResult =
  | { kind: 'scheduled'; deletion: PendingDeletion }
  | { kind: 'deleted' };

/** What happened to the plan when the account came back. */
export type RestoredSubscription = 'resumed' | 'ended' | 'none';

export interface DataExport {
  jobId: string;
  status: 'queued' | 'running' | 'ready' | 'failed';
  createdAt: string;
  readyAt?: string;
  sizeBytes?: number;
  /** The file is kept until then. */
  expiresAt?: string;
  /** A short-lived link to the zip, present while it is ready. */
  downloadUrl?: string;
}

export class WrongPasswordError extends Error {
  constructor() {
    super("That password isn't right.");
    this.name = 'WrongPasswordError';
  }
}

async function readBody(response: Response): Promise<any> {
  return response.json().catch(() => ({}));
}

/** Ask to delete the signed-in account (owner: scheduled; team member: now). */
export const requestAccountDeletion = async (password: string): Promise<DeletionResult> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}api/users/me/deletion`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ password }),
  });
  const body = await readBody(response);
  if (response.status === 401 && body?.code === 'WRONG_PASSWORD') throw new WrongPasswordError();
  if (!response.ok) throw new Error(body?.message || "We couldn't delete the account. Please try again.");
  return body?.deletion ? { kind: 'scheduled', deletion: body.deletion } : { kind: 'deleted' };
};

/** Cancel a scheduled deletion: the owner's account comes back. */
export const restoreAccount = async (): Promise<RestoredSubscription> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}api/users/me/deletion`, {
    method: 'DELETE',
    headers,
  });
  const body = await readBody(response);
  if (!response.ok) throw new Error(body?.message || "We couldn't restore the account. Please try again.");
  return (body?.subscription as RestoredSubscription) ?? 'none';
};

/** The account's latest data export, or null when there is none. */
export const getDataExport = async (): Promise<DataExport | null> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}api/users/me/export`, { headers });
  const body = await readBody(response);
  if (!response.ok) throw new Error(body?.message || "We couldn't check your download.");
  return body?.export ?? null;
};

/** Start building a new export of the account's data. */
export const startDataExport = async (): Promise<DataExport> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}api/users/me/export`, { method: 'POST', headers });
  const body = await readBody(response);
  if (!response.ok) throw new Error(body?.message || "We couldn't start your download. Please try again.");
  return body.export as DataExport;
};

/** "2.4 MB", "640 KB". */
export const formatBytes = (bytes?: number): string => {
  if (!bytes || bytes < 1024) return `${bytes ?? 0} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** "3 December 2026". */
export const formatLongDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
